import React from 'react';
import { useForm } from 'react-hook-form';
import { Save, Cancel } from '@mui/icons-material';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Stack, Divider, Button } from '@mui/material';
import { convertEnumToSelectOptions } from 'utils/selectOptionsHelper';
import { DealActivityStatus, DealActivityEventType, DealActivityEventPriority } from '@trnsact/trnsact-shared-types';
import {
  DateField,
  InputField,
  SelectField,
  DateTimeField,
  CheckboxField,
  AutocompleteValueField,
} from 'components/form';

import { CalendarFormMapper } from '../../mapper';
import { CalendarEventCardProps } from '../../types';
import { useDealActivityMutations } from '../../../api';
import { calendarApi, useCalendarFormOptions } from '../../api';
import { calendarEventUpdateFormSchema, CalendarEventUpdateFormValues } from './calendarEventUpdateFormSchema';

export const CalendarEventUpdateForm = ({ event, onSwitchMode }: CalendarEventCardProps) => {
  const { handleUpdateDealActivity, isDealActivityUpdating } = useDealActivityMutations();
  const { accountOptions, accountOptionsLoading } = useCalendarFormOptions();

  const { control, watch, setValue, handleSubmit } = useForm<CalendarEventUpdateFormValues>({
    resolver: zodResolver(calendarEventUpdateFormSchema),
    defaultValues: {
      subject: event.subject,
      priority: event.priority,
      allDay: event.allDay,
      eventType: event.eventType,
      accountId: event.accountId,
      endAt: event.endAt ?? null,
      startAt: event.startAt ?? null,
      endDate: event.endDate ?? null,
      startDate: event.startDate ?? null,
      activityStatus: event.activityStatus,
      description: event.description ?? '',
    },
  });

  const allDay = watch('allDay');

  const onSubmit = async (data: CalendarEventUpdateFormValues) => {
    await handleUpdateDealActivity(CalendarFormMapper.mapFormDtoToUpdate(event, data), () => ({
      awaitRefetchQueries: true,
      refetchQueries: [calendarApi.dealActivity, calendarApi.listOfCalendarEvents],
    }));

    onSwitchMode();
  };

  return (
    <>
      <Stack height="100%" direction="column" gap="1rem">
        <InputField
          label="Subject"
          name="subject"
          control={control}
          inputProps={{ fullWidth: true, variant: 'outlined' }}
        />

        <Divider sx={{ borderColor: 'divider' }} />

        <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <AutocompleteValueField
            name="accountId"
            control={control}
            options={accountOptions}
            loading={accountOptionsLoading}
            textFieldProps={{ label: 'Account', variant: 'outlined', size: 'small' }}
          />

          <SelectField
            label="Type"
            name="eventType"
            control={control}
            variant="outlined"
            options={convertEnumToSelectOptions(DealActivityEventType)}
          />
        </Box>

        <CheckboxField
          label="All day"
          name="allDay"
          control={control}
          extraAction={checked => {
            if (checked) {
              setValue('startAt', null);
              setValue('endAt', null);
            } else {
              setValue('startDate', null);
              setValue('endDate', null);
            }
          }}
        />

        <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          {allDay ? (
            <DateField
              label="Start date"
              name="startDate"
              control={control}
              slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
            />
          ) : (
            <DateTimeField
              label="Start"
              name="startAt"
              control={control}
              slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
            />
          )}

          {allDay ? (
            <DateField
              label="End date"
              name="endDate"
              control={control}
              slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
            />
          ) : (
            <DateTimeField
              label="End"
              name="endAt"
              control={control}
              slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
            />
          )}
        </Box>

        <Box sx={{ display: 'grid', gridGap: '1rem', gridTemplateColumns: '1fr 1fr' }}>
          <SelectField
            label="Status"
            control={control}
            variant="outlined"
            name="activityStatus"
            options={convertEnumToSelectOptions(DealActivityStatus)}
          />

          <SelectField
            label="Priority"
            name="priority"
            control={control}
            variant="outlined"
            options={convertEnumToSelectOptions(DealActivityEventPriority)}
          />
        </Box>

        <InputField
          label="Description"
          name="description"
          control={control}
          inputProps={{
            minRows: 3,
            multiline: true,
            fullWidth: true,
            variant: 'outlined',
            placeholder: 'Add description...',
          }}
        />
      </Stack>

      <Stack direction="row" gap="1rem" width="100%">
        <Button
          fullWidth
          variant="contained"
          startIcon={<Save />}
          onClick={handleSubmit(onSubmit)}
          loading={isDealActivityUpdating}
        >
          Save
        </Button>

        <Button fullWidth onClick={onSwitchMode} startIcon={<Cancel />} disabled={isDealActivityUpdating}>
          Cancel
        </Button>
      </Stack>
    </>
  );
};
